// アプリメニューの「キャンバスのリンクをコピー」ボタン。
// キャンバス名付きのリンクをクリップボードへ書き込み、結果を画面右下のお知らせで通知する。
// - Markdown（text/plain）とアンカー 1 個の HTML（text/html）を 1 回の書き込みで併記し、
//   Markdown を解釈する貼り付け先でもリッチテキストの貼り付け先でも表示文言がリンクになるようにする。
// - 文字列の組み立ては純粋関数（lib/canvas-link-clipboard）に寄せ、ここはイベント境界だけを担う。
// - クリップボードは OS への書き込みであり更新系 API ではないため、楽観的な成功表示は行わない。
// - コピーのためのサーバー要求・共有トークン発行は行わない。閲覧できる利用者なら誰でも操作できる。
import { LinkIconData } from "@meiden-connect/material-symbols";
import { Button, Icon } from "@meiden-connect/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CanvasLinkClipboardPayload } from "../../../lib/canvas-link-clipboard";
import { buildCanvasLinkClipboardPayload } from "../../../lib/canvas-link-clipboard";
import { useShowToast } from "../notice/use-show-toast";

/** ボタンのツールチップとアクセシブル名（両方に同じ文言を使う）。 */
const COPY_ACTION_LABEL = "キャンバスのリンクをコピー";
/** クリップボードへの書き込みが完了したときの通知文言。 */
const COPY_SUCCEEDED_MESSAGE = "キャンバスのリンクをコピーしました。";
/** Clipboard API 未対応・権限拒否・例外のいずれでも共通の失敗通知文言。 */
const COPY_FAILED_MESSAGE = "クリップボードへコピーできませんでした。";

export interface CanvasLinkCopyButtonProps {
  /** 表示用に正規化済みのキャンバス名（未確定の編集内容ではなく、確定済みの名前を渡す）。 */
  readonly title: string;
}

/**
 * 2 形式を併記する `ClipboardItem` を組み立てる。
 *
 * `undefined` を返すのは「まだクリップボードへ 1 バイトも書いていない」場合だけなので、
 * 呼び出し側は安全に `writeText`（text/plain のみ）へ退避できる。
 *
 * @param payload クリップボードへ入れる Markdown と HTML
 * @returns 併記用の `ClipboardItem`。組み立てられない実行環境では `undefined`
 */
function createRichClipboardItem(payload: CanvasLinkClipboardPayload): ClipboardItem | undefined {
  // 非 secure context や未対応ブラウザーには ClipboardItem が無い。
  // 単体テストから差し替えられるよう、必ず globalThis 経由で参照する。
  if (typeof globalThis.ClipboardItem !== "function" || payload.html.length === 0) {
    return undefined;
  }
  try {
    // ブラウザーによってはキー側の MIME で判定するため、Blob の type とキーをそろえる。
    // Blob は UTF-8 として直列化されるため、日本語のキャンバス名もそのまま扱える。
    return new globalThis.ClipboardItem({
      "text/html": new Blob([payload.html], { type: "text/html" }),
      "text/plain": new Blob([payload.markdown], { type: "text/plain" }),
    });
  } catch {
    // ClipboardItem / Blob の生成自体が throw する実装がある。書き込み前なので退避できる。
    return undefined;
  }
}

export function CanvasLinkCopyButton({ title }: CanvasLinkCopyButtonProps): JSX.Element {
  const showToast = useShowToast();
  const [copying, setCopying] = useState(false);
  // 同一描画中の連打も止めるため、state の disabled 表示とは別に同期的なガードを持つ。
  const copyingRef = useRef(false);
  // 別のキャンバスへ遷移してアンマウントされた後に、旧キャンバスのコピー結果を通知しないためのフラグ。
  const mountedRef = useRef(true);
  // disabled になった瞬間にブラウザーがフォーカスを外すため、書き込み後に戻す必要があるかを覚える。
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const refocusRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (copying || !refocusRef.current) {
      return;
    }
    refocusRef.current = false;
    // 利用者が自分で別の場所へフォーカスを移した場合は奪い返さない。
    // disabled 化で外れたときだけ（= body へ落ちているときだけ）同じボタンへ戻す。
    // preventScroll: 表示位置を変えないことがこの機能の要件のため、戻すのはフォーカスだけにする。
    if (document.activeElement === null || document.activeElement === document.body) {
      buttonRef.current?.focus({ preventScroll: true });
    }
  }, [copying]);

  const handleClick = useCallback((): void => {
    if (copyingRef.current) {
      return;
    }
    copyingRef.current = true;
    // キーボード操作を続けられるよう、押下時にフォーカスを持っていたかを控える。
    refocusRef.current = buttonRef.current !== null && document.activeElement === buttonRef.current;
    setCopying(true);
    /** 成功・拒否・例外のどれでも必ず連打ガードを解除し、生存中だけ通知する。 */
    const finish = (level: "success" | "warning", message: string): void => {
      copyingRef.current = false;
      if (!mountedRef.current) {
        return;
      }
      setCopying(false);
      showToast(level, message);
    };
    try {
      // クリック時点のキャンバス名と URL を 1 組として確定する（処理中の移動で混ざらないようにする）。
      // URL は表示位置・ズームのクエリを含めてそのまま使う。
      const payload = buildCanvasLinkClipboardPayload({ href: window.location.href, title });
      // 非 secure context や未対応ブラウザーでは navigator.clipboard 自体が存在しない。
      const clipboard: Clipboard | undefined = navigator.clipboard;
      if (typeof clipboard?.write === "function") {
        const richItem = createRichClipboardItem(payload);
        if (richItem !== undefined) {
          // write が rejection を返した後に writeText を試してはいけない。権限拒否なら writeText も
          // 拒否されるうえ、write が部分的に成功していた場合はクリップボードを 1 形式へ上書きし、
          // 利用者から見て何が入ったか分からない状態を作るため。
          // 権限拒否は rejection、実装によっては同期 throw で来るが、後者も外側の catch が失敗として扱う。
          void clipboard.write([richItem]).then(
            () => finish("success", COPY_SUCCEEDED_MESSAGE),
            () => finish("warning", COPY_FAILED_MESSAGE)
          );
          return;
        }
      }
      // ここへ来るのは「書き込む前に併記が使えないと分かった場合」だけ。text/plain だけで退避する。
      if (typeof clipboard?.writeText !== "function") {
        finish("warning", COPY_FAILED_MESSAGE);
        return;
      }
      // 権限拒否は rejection、実装によっては同期 throw で来るため、両方を受ける。
      void clipboard.writeText(payload.markdown).then(
        () => finish("success", COPY_SUCCEEDED_MESSAGE),
        () => finish("warning", COPY_FAILED_MESSAGE)
      );
    } catch {
      finish("warning", COPY_FAILED_MESSAGE);
    }
  }, [showToast, title]);

  return (
    <Button
      type="button"
      // クリック直後にクリップボードへ書き込む実行ボタンなので action を使う。
      variant="action"
      className="canvas-icon-button"
      // 連打で 2 回書き込まれないよう、書き込みが終わるまで無効化する。
      disabled={copying}
      aria-label={COPY_ACTION_LABEL}
      title={COPY_ACTION_LABEL}
      onClick={handleClick}
      ref={buttonRef}
    >
      <Icon aria-hidden="true" d={LinkIconData} />
    </Button>
  );
}
